import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Wrench, Check, Plus, X } from "lucide-react";

const fmt = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

/**
 * RefurbEstimator
 * - Loads the refurb library
 * - Shows checkboxes per item, grouped by category
 * - Each checked item has a qty input (defaults to typical_qty or 1)
 * - Total auto-computes; "Apply to property" copies into actual_renovation_spend (estimate)
 *
 * props:
 *   - selected (array of {item_id, name, unit_cost, qty, subtotal})
 *   - onChange(newSelected, total)
 */
export default function RefurbEstimator({ selected = [], onChange, onApplyTotal }) {
  const [library, setLibrary] = useState([]);
  const [picked, setPicked] = useState({}); // item_id -> {qty, price_override}
  const [custom, setCustom] = useState([]); // ad-hoc line items {id,name,unit_cost,qty}
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customForm, setCustomForm] = useState({ name: "", unit_cost: "", qty: 1 });
  const lastEmittedRef = useRef(null); // signature of the last rows we emitted
  const hydratedRef = useRef(false);

  useEffect(() => {
    api.get("/refurb-items").then(r => setLibrary(r.data || [])).catch(() => {});
  }, []);

  // hydrate from props.selected — but skip when the incoming prop is just a
  // reflection of what we ourselves emitted (parent → child echo), otherwise
  // typing into a price override causes an infinite render loop.
  useEffect(() => {
    const incomingSig = JSON.stringify(
      (selected || []).map(s => ({
        k: s.item_id || s.custom_id,
        n: s.name,
        q: Number(s.qty) || 1,
        u: s.price_override != null ? Number(s.price_override) : Number(s.unit_cost) || 0,
      }))
    );
    if (hydratedRef.current && incomingSig === lastEmittedRef.current) return;
    const map = {};
    const c = [];
    for (const s of selected) {
      if (s.item_id) map[s.item_id] = { qty: Number(s.qty) || 1, price_override: s.price_override != null ? Number(s.price_override) : null };
      else c.push({ id: s.custom_id || `custom-${Math.random().toString(36).slice(2, 9)}`, name: s.name, unit_cost: Number(s.unit_cost) || 0, qty: Number(s.qty) || 1 });
    }
    setPicked(map);
    setCustom(c);
    hydratedRef.current = true;
  }, [selected]);

  const grouped = useMemo(() => {
    const g = {};
    for (const it of library) (g[it.category || "general"] ||= []).push(it);
    return g;
  }, [library]);

  const rows = useMemo(() => {
    const libraryRows = library
      .filter(it => picked[it.id] !== undefined)
      .map(it => {
        const p = picked[it.id];
        const unit = p.price_override != null ? p.price_override : (Number(it.unit_cost) || 0);
        const qty = Number(p.qty) || 1;
        return {
          item_id: it.id, name: it.name, unit_cost: unit, unit: it.unit, qty,
          subtotal: unit * qty,
          price_override: p.price_override,
        };
      });
    const customRows = custom.map(c => ({
      custom_id: c.id, name: c.name, unit_cost: c.unit_cost, unit: "each",
      qty: c.qty, subtotal: c.unit_cost * c.qty,
    }));
    return [...libraryRows, ...customRows];
  }, [library, picked, custom]);

  const total = rows.reduce((s, r) => s + r.subtotal, 0);

  useEffect(() => {
    // record the signature we're about to emit so the hydration effect can
    // ignore the echo when the parent re-passes the same data back down.
    lastEmittedRef.current = JSON.stringify(
      rows.map(r => ({
        k: r.item_id || r.custom_id,
        n: r.name,
        q: Number(r.qty) || 1,
        u: r.price_override != null ? Number(r.price_override) : Number(r.unit_cost) || 0,
      }))
    );
    if (onChange) onChange(rows, total);
  }, [rows.map(r => `${r.item_id || r.custom_id}:${r.qty}:${r.unit_cost}`).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id, defaultQty) => {
    setPicked(prev => {
      const next = { ...prev };
      if (next[id] !== undefined) delete next[id];
      else next[id] = { qty: Number(defaultQty) || 1, price_override: null };
      return next;
    });
  };
  const setQty = (id, q) => setPicked(prev => ({ ...prev, [id]: { ...prev[id], qty: Number(q) || 0 } }));
  const setOverride = (id, val) => setPicked(prev => ({
    ...prev,
    [id]: { ...prev[id], price_override: val === "" ? null : Number(val) },
  }));

  const addCustom = (e) => {
    e.preventDefault();
    if (!customForm.name || !customForm.unit_cost) return;
    setCustom(c => [...c, { id: `custom-${Date.now()}`, name: customForm.name, unit_cost: Number(customForm.unit_cost), qty: Number(customForm.qty) || 1 }]);
    setCustomForm({ name: "", unit_cost: "", qty: 1 });
    setShowAddCustom(false);
  };
  const removeCustom = (id) => setCustom(c => c.filter(x => x.id !== id));

  const CATS = [
    ["kitchen", "Kitchen"], ["bathroom", "Bathroom"], ["structural", "Structural"],
    ["systems", "Systems"], ["exterior", "Exterior"], ["finishes", "Finishes"], ["general", "General"],
  ];

  return (
    <div className="luxury-card-ruby p-6 space-y-5" data-testid="refurb-estimator">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-serif text-ivory">Refurb estimator</h2>
          <p className="text-xs text-ivory/50 mt-1">
            Tick what this house needs. Override the price inline if this job costs more than the library default.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowAddCustom(s => !s)} className="btn-outline !py-1 !px-3 !text-[0.65rem]" data-testid="refurb-add-custom-toggle">
            {showAddCustom ? <X size={11}/> : <Plus size={11}/>} Custom line
          </button>
          <Link to="/admin/refurb-library" className="btn-outline !py-1 !px-3 !text-[0.65rem]" data-testid="link-refurb-library">
            <Wrench size={11} /> Library
          </Link>
        </div>
      </div>

      {showAddCustom && (
        <form onSubmit={addCustom} className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="refurb-custom-form">
          <input className="md:col-span-2 bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Line item (e.g. Chimney repair)" value={customForm.name} onChange={e => setCustomForm({ ...customForm, name: e.target.value })} data-testid="custom-name" />
          <input type="number" className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-right" placeholder="£ cost" value={customForm.unit_cost} onChange={e => setCustomForm({ ...customForm, unit_cost: e.target.value })} data-testid="custom-cost" />
          <input type="number" className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-right" placeholder="qty" value={customForm.qty} onChange={e => setCustomForm({ ...customForm, qty: e.target.value })} />
          <button className="btn-ruby justify-center" data-testid="custom-submit"><Plus size={11}/> Add</button>
        </form>
      )}

      {custom.length > 0 && (
        <div className="border border-bronze/30 p-3">
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-bronze mb-2">Custom lines · this property only</p>
          <div className="space-y-1.5">
            {custom.map(c => (
              <div key={c.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-ivory/85 flex-1 truncate">{c.name}</span>
                <input type="number" value={c.unit_cost} onChange={e => setCustom(cs => cs.map(x => x.id === c.id ? { ...x, unit_cost: Number(e.target.value) || 0 } : x))}
                  className="w-24 bg-charcoal-800 border border-charcoal-600 px-2 py-1 text-xs text-right" />
                <span className="text-ivory/40 text-xs">× </span>
                <input type="number" value={c.qty} onChange={e => setCustom(cs => cs.map(x => x.id === c.id ? { ...x, qty: Number(e.target.value) || 0 } : x))}
                  className="w-14 bg-charcoal-800 border border-charcoal-600 px-1.5 py-1 text-xs text-right" />
                <span className="text-gold text-xs font-mono w-24 text-right">{fmt(c.unit_cost * c.qty)}</span>
                <button type="button" onClick={() => removeCustom(c.id)} className="text-ruby-light hover:text-ruby" data-testid={`refurb-remove-custom-${c.id}`}><X size={12}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {library.length === 0 && <p className="text-ivory/40 text-sm">Loading library…</p>}

      {CATS.map(([key, label]) => {
        const items = grouped[key];
        if (!items || items.length === 0) return null;
        return (
          <div key={key} className="border-t border-charcoal-600 pt-4 first:border-t-0 first:pt-0">
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mb-3">{label}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {items.map(it => {
                const p = picked[it.id];
                const isOn = p !== undefined;
                const shown = isOn && p.price_override != null ? p.price_override : it.unit_cost;
                return (
                  <div key={it.id}
                       className={`flex items-center justify-between gap-2 px-2 py-1.5 border ${isOn ? "border-gold/40 bg-ink-950/30" : "border-charcoal-600/60"}`}
                       data-testid={`refurb-pick-${it.id}`}>
                    <label className="flex items-center gap-2 flex-1 cursor-pointer text-sm text-ivory/85 min-w-0">
                      <input type="checkbox" checked={isOn} onChange={() => toggle(it.id, it.typical_qty)}
                        className="accent-ruby" data-testid={`refurb-toggle-${it.id}`} />
                      <span className="truncate">{it.name}</span>
                    </label>
                    {isOn ? (
                      <>
                        <input
                          type="number"
                          value={p.price_override != null ? p.price_override : it.unit_cost}
                          onChange={(e) => setOverride(it.id, e.target.value)}
                          className={`w-20 bg-charcoal-800 border ${p.price_override != null ? "border-bronze" : "border-charcoal-600"} px-1.5 py-0.5 text-xs text-right`}
                          data-testid={`refurb-price-${it.id}`}
                          title={p.price_override != null ? "Overridden for this property" : "Library default — edit to override"}
                        />
                        <span className="text-[0.6rem] text-ivory/40 whitespace-nowrap">/{it.unit}</span>
                        <input type="number" min={0} value={p.qty}
                          onChange={(e) => setQty(it.id, e.target.value)}
                          className="w-12 bg-charcoal-800 border border-charcoal-600 px-1.5 py-0.5 text-xs text-right"
                          data-testid={`refurb-qty-${it.id}`} />
                      </>
                    ) : (
                      <span className="text-[0.65rem] text-ivory/40 whitespace-nowrap">{fmt(shown)}/{it.unit}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="border-t border-ruby/40 pt-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/50">{rows.length} items selected</p>
            <p className="font-serif text-3xl text-ivory mt-1" data-testid="refurb-total">{fmt(total)}</p>
          </div>
          <button type="button" onClick={() => onApplyTotal?.(total)} disabled={total === 0}
            className="btn-ruby disabled:opacity-30" data-testid="refurb-apply">
            <Check size={13} /> Apply to renovation spend
          </button>
        </div>
      </div>
    </div>
  );
}
